import { Empty, Spin, message } from "antd";
import { Plus, Search, X } from "lucide-react";
import { FC, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDebounceCallback } from "~/hooks/use-debounce-callback";
import { TInventory } from "~/types";

interface InventoryListProps {
  inventories: TInventory[];
  isLoading: boolean;
  onAddInventory: (inventoryId: string) => void;
  onRemoveInventory: (inventoryId: string) => void;
  isInventoryAssigned: (inventoryId: string) => boolean;
  hasServiceSelected: boolean;
  loadMore: () => void;
  hasMore: boolean;
  onSearch: (term: string) => void;
  selectedClientId?: string;
}

export const InventoryList: FC<InventoryListProps> = ({
  inventories,
  isLoading,
  onAddInventory,
  onRemoveInventory,
  isInventoryAssigned,
  hasServiceSelected,
  loadMore,
  hasMore,
  onSearch,
  selectedClientId,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedCallback = useDebounceCallback(onSearch, 500);

  // Push the search term to parent when it changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedCallback(value);
  };

  const getAvailabilityBadge = (available: boolean) => {
    if (available) {
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
          Available
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
        Assigned
      </span>
    );
  };

  const handleAddInventory = (inventoryId: string) => {
    const inventoryItem = inventories.find((inv) => inv.id === inventoryId);

    if (selectedClientId && inventoryItem?.client?.id !== selectedClientId) {
      message.error("Inventory item must belong to the same client.");
      return;
    }

    onAddInventory(inventoryId);
  };

  return (
    <div className="p-4">
      <div className="mb-4 relative">
        <input
          type="text"
          className="w-full pl-10 pr-4 py-2 border rounded-lg"
          placeholder="Search inventory..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
      </div>

      {!hasServiceSelected ? (
        <Empty description="Please select a ticket" />
      ) : (
        <Spin spinning={isLoading && inventories.length === 0}>
          <InfiniteScroll
            dataLength={inventories.length}
            next={loadMore}
            hasMore={hasMore}
            loader={<Spin className="m-5" />}
            height={384}
            endMessage={
              <p className="text-center text-gray-500 mt-4">
                {inventories.length === 0
                  ? "No inventory items found"
                  : "No more inventory items to load"}
              </p>
            }
          >
            {inventories.map((item) => (
              <div
                key={item.id}
                className="flex justify-between items-center p-3 border rounded-lg m-2"
              >
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">{item.inventoryRef}</span>
                    <span className="text-sm text-gray-500">
                      {item.client?.companyName ||
                        [item.client?.firstName, item.client?.lastName]
                          .filter(Boolean)
                          .join(" ")
                          .trim() ||
                        "Unnamed Client"}
                    </span>
                  </div>
                  <div className="mt-1">{item.title}</div>
                  <div className="text-sm text-gray-500">
                    {item.length
                      ? `${item.length}" × ${item.width}" × ${item.height}"`
                      : "No dimensions"}
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {getAvailabilityBadge(!isInventoryAssigned(item.id))}
                  {hasServiceSelected && !isInventoryAssigned(item.id) && (
                    <button
                      className="p-1 text-blue-600 hover:text-blue-800"
                      onClick={() => handleAddInventory(item.id)}
                    >
                      <Plus size={16} />
                    </button>
                  )}
                  {hasServiceSelected && isInventoryAssigned(item.id) && (
                    <button
                      className="p-1 text-red-600 hover:text-red-800"
                      onClick={() => onRemoveInventory(item.id)}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </InfiniteScroll>
        </Spin>
      )}

      <div className="mt-4 pt-4 border-t">
        <button className="w-full flex items-center justify-center space-x-2 p-3 border rounded-lg hover:bg-gray-50">
          <Plus size={16} />
          <span>Add New Inventory Item</span>
        </button>
      </div>
    </div>
  );
};
